import { useState, useEffect, useRef } from "react";
import { Box, Typography, Paper, IconButton } from "@mui/material";
import { LocationOn, MoreVert } from "@mui/icons-material";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import { useTranslation } from "react-i18next";
import { CompanyInfo } from "../../Models/company";
import { useTerminology } from "../../Core/useTerminology";
import { getCompanyIconElement } from "../../Helpers/iconHelpers";
import colors from "../../Constants/colors";
import { useExportCompany } from "../../Query/useCompanies";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import { useWorkspaceShareCompany } from "../../Query/useCompanies";
import { UserRole } from "../../Enums/companyStatus";
import { hasRole } from "../../Helpers/authHelpers";
import { useCurrentWorkspace } from "../../Query/useWorkspace";

export interface CompanyInfoCardProps {
  company: CompanyInfo;
  onEdit?: () => void;
  onDelete?: () => void;
}

export function CompanyInfoCard({
  company,
  onEdit,
  onDelete,
}: CompanyInfoCardProps) {
  const { t } = useTranslation("singleCompanyComponent");
  const terminology = useTerminology();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const isAdmin = hasRole(UserRole.ADMIN);
  const { mutate: exportCompanyMutate, isPending: isExportCompanyPending } =
    useExportCompany();
  const workspaceShareMutation = useWorkspaceShareCompany();
  const { data: currentWorkspace } = useCurrentWorkspace();

  const hasWorkspace = !!currentWorkspace?.workspace?.id;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleWorkspaceShare = () => {
    setMenuOpen(false);
    workspaceShareMutation.mutate(company.id.toString());
  };

  const handleEdit = () => {
    setMenuOpen(false);
    onEdit?.();
  };

  const handleDelete = () => {
    setMenuOpen(false);
    onDelete?.();
  };

  const handleExportCompany = () => {
    if (!company?.id) return;

    const safeCompanyName = (company.name || "company")
      .trim()
      .replace(/[^a-zA-Z0-9-_]+/g, "_");

    exportCompanyMutate(
      { companyId: String(company.id) },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(response, `${safeCompanyName}_export.zip`);
        },
      },
    );

    setMenuOpen(false);
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        mb: 3,
        borderRadius: 2,
        border: `1px solid ${colors.gray200}`,
        borderLeft: `4px solid ${colors.primary_dark}`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 600,
                  color: colors.primary_dark,
                  marginBottom: 0,
                }}
              >
                {company.name}
              </Typography>
              {getCompanyIconElement(company.icon_id, {
                color: colors.accent,
                fontSize: "2.5rem",
              })}
              {company.workspace_shared && (
                <Typography
                  variant="caption"
                  sx={{
                    backgroundColor: colors.primary_dark,
                    color: colors.white,
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    fontWeight: 500,
                  }}
                >
                  {t("singleCompanyComponent.workspaceShared")}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ position: "relative" }} ref={menuRef}>
          <IconButton
            onClick={() => setMenuOpen(!menuOpen)}
            sx={{ "&:hover": { bgcolor: colors.gray100 } }}
          >
            <MoreVert sx={{ color: colors.gray500 }} />
          </IconButton>
          {menuOpen && (
            <Paper
              elevation={3}
              sx={{
                position: "absolute",
                zIndex: 10,
                right: 0,
                top: "100%",
                mt: 0.5,
                minWidth: 180,
                borderRadius: 2,
                border: `1px solid ${colors.gray200}`,
                borderLeft: `4px solid ${colors.primary_dark}`,
                overflow: "hidden",
              }}
            >
              <Box
                onClick={handleEdit}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  bgcolor: "white",
                  borderLeft: "4px solid transparent",
                  "&:hover": {
                    bgcolor: colors.gray50,
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: colors.primary_dark,
                  }}
                >
                  {t("companyActions.editEntityInfo", {
                    entity: terminology.genitive,
                  })}
                </Typography>
              </Box>
              <Box
                onClick={handleExportCompany}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  bgcolor: "white",
                  borderLeft: "4px solid transparent",
                  "&:hover": {
                    bgcolor: colors.gray50,
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: colors.primary_dark,
                  }}
                >
                  {isExportCompanyPending
                    ? t("companyActions.exporting")
                    : t("companyActions.exportCompany", {
                        entity: terminology.singular,
                      })}
                </Typography>
              </Box>
              {!company.workspace_shared && !isAdmin && hasWorkspace && (
                <Box
                  onClick={handleWorkspaceShare}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    bgcolor: "white",
                    borderLeft: "4px solid transparent",
                    "&:hover": {
                      bgcolor: colors.gray50,
                    },
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: colors.primary_dark,
                    }}
                  >
                    {t("companyActions.shareWorkspace", {
                      entity: terminology.singular,
                    })}
                  </Typography>
                </Box>
              )}
              <Box
                onClick={handleDelete}
                sx={{
                  px: 2,
                  py: 1.5,
                  cursor: "pointer",
                  bgcolor: "white",
                  borderLeft: "4px solid transparent",
                  "&:hover": {
                    bgcolor: colors.gray50,
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: colors.red600,
                  }}
                >
                  {t("companyActions.deleteEntity", {
                    entity: terminology.singular,
                  })}
                </Typography>
              </Box>
            </Paper>
          )}
        </Box>
      </Box>
      {company.industry && (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 2 }}
        >
          <BusinessRoundedIcon
            sx={{ color: colors.accent, fontSize: "1.5rem" }}
          />
          <Typography variant="subtitle1" sx={{ color: colors.primary_dark }}>
            {company.industry}
          </Typography>
        </Box>
      )}
      {company.location && (
        <Box
          sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2, mt: 2 }}
        >
          <LocationOn sx={{ color: colors.accent, fontSize: "1.5rem" }} />
          <Typography variant="subtitle1" sx={{ color: colors.primary_dark }}>
            {company.location}
          </Typography>
        </Box>
      )}

      <Typography
        sx={{
          fontSize: "1rem",
          color: colors.gray700,
          lineHeight: 1.7,
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {company.about}
      </Typography>
    </Paper>
  );
}
